"use client";

import React, { useRef, ReactNode, useEffect, useState, Children } from "react";
import { Link } from "@cappelendamm/design-system/react";
import { useAccordion } from "../../components/useAccordion";
import classNames from "classnames";

interface ExpandableTextProps {
  maxLines?: number;
  children?: ReactNode;
  truncated?: ReactNode;
  isFullWidth?: boolean;
}

const LINK_HEIGHT = 40;

export function ExpandableContainer({
  maxLines: initialMaxLines = 10,
  truncated,
  children,
  isFullWidth = false,
}: ExpandableTextProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<{
    canTruncate: boolean;
    maxLines: number | "unset";
  }>({
    canTruncate: Children.count(children) !== Children.count(truncated),
    maxLines: initialMaxLines,
  });
  const { toggle, aria, isExpanded } = useAccordion(false);

  useEffect(() => {
    if (textRef.current) {
      const contentDiff =
        textRef.current.scrollHeight - textRef.current.clientHeight;
      if (contentDiff > LINK_HEIGHT) {
        setState((prev) => ({ ...prev, canTruncate: true }));
      } else if (contentDiff > 0) {
        setState((prev) => ({
          ...prev,
          maxLines: "unset",
        }));
      }
    }
  }, [textRef, setState]);

  const handleToggle = () => {
    if (isExpanded) {
      containerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
    toggle();
  };

  return (
    <div ref={containerRef} className="grid">
      <Link
        as="button"
        onClick={handleToggle}
        className={classNames("order-last h-10 w-fit p-0", {
          hidden: !state.canTruncate,
        })}
        {...(state.canTruncate ? aria.button : {})}
      >
        {isExpanded ? "Les mindre" : "Les mer"}
      </Link>
      <div
        {...(state.canTruncate ? aria.panel : {})}
        ref={textRef}
        className="prose [&>*+*]:mb-4"
        style={
          {
            display: isExpanded ? "block" : "-webkit-box",
            WebkitLineClamp: isExpanded ? "unset" : state.maxLines,
            WebkitBoxOrient: "vertical",
            overflow: isExpanded ? "visible" : "hidden",

            "--cvr-prose-width": isFullWidth ? "100%" : "unset",
          } as React.CSSProperties
        }
      >
        {isExpanded ? children : truncated}
      </div>
    </div>
  );
}
